import { createFileRoute } from '@tanstack/react-router'

import {
  getGetCreditBalanceQueryKey,
  useBuyPackage,
  useGetCreditBalance,
  useGetPackage,
} from '../../api/generated/endpoints/credits/credits'
import { useQueryClient } from '@tanstack/react-query'
import { CREDIT_REASON_LABELS, formatHours, packageSaving } from '../../lib/credits'
import { apiMessage } from '../../lib/api-error'
import { formatUYU } from '../../lib/money'
import { formatInstant } from '../../lib/time'

export const Route = createFileRoute('/_authed/packages')({
  component: PackagesPage,
})

function PackagesPage() {
  const queryClient = useQueryClient()
  const packageInfo = useGetPackage()
  const balance = useGetCreditBalance()

  const buy = useBuyPackage({
    mutation: {
      // Never auto-retry a purchase.
      retry: false,
      onSuccess: async (session) => {
        await queryClient.invalidateQueries({ queryKey: getGetCreditBalanceQueryKey() })
        // The provider decides where they go to pay.
        window.location.href = session.checkout_url
      },
    },
  })

  if (packageInfo.isPending) return <p className="text-slate-600">Cargando…</p>

  const pkg = packageInfo.data
  const saving = pkg
    ? packageSaving(pkg.price_uyu_pesos, pkg.hours, pkg.single_hour_price_uyu_pesos)
    : 0
  const entries = balance.data?.entries ?? []

  return (
    <div className="max-w-lg space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Comprar horas</h1>
        <p className="mt-1 text-slate-600">
          Horas pagas por adelantado, para usar cuando venga bien.
        </p>
      </div>

      {/* The balance below renders whatever happens up here. Somebody's own
          hours are a separate fact from whether packages are on sale today,
          and hiding them behind this query was how an unsold package took the
          whole page down. */}
      {packageInfo.isError ? (
        <p role="alert" className="text-red-600">
          No se pudieron cargar los datos del paquete.
        </p>
      ) : !pkg?.sold ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-slate-600">
          <p>Por ahora no hay paquetes de horas a la venta.</p>
          <p className="mt-1 text-sm">
            Las horas se pagan por sesión
            {pkg && pkg.single_hour_price_uyu_pesos > 0 && (
              <>, a {formatUYU(pkg.single_hour_price_uyu_pesos)} cada una</>
            )}
            .
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="flex items-baseline justify-between">
            <h2 className="text-lg font-semibold">{formatHours(pkg.hours)}</h2>
            <p className="text-2xl font-semibold">{formatUYU(pkg.price_uyu_pesos)}</p>
          </div>
          <p className="mt-1 text-sm text-slate-600">
            {formatUYU(Math.round(pkg.price_uyu_pesos / pkg.hours))} la hora, contra{' '}
            {formatUYU(pkg.single_hour_price_uyu_pesos)} pagando por sesión
            {saving > 0 && <> &mdash; ahorro del {saving}%</>}.
          </p>

          <button
            onClick={() => buy.mutate()}
            disabled={buy.isPending}
            className="mt-5 min-h-12 w-full rounded-md bg-slate-900 px-4 text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {buy.isPending ? 'Iniciando…' : `Comprar ${formatHours(pkg.hours)}`}
          </button>

          {buy.isError && (
            <p role="alert" className="mt-3 text-sm text-red-600">
              {apiMessage(buy.error, 'No se pudo iniciar la compra.')}
            </p>
          )}

          <p className="mt-3 text-center text-xs text-slate-500">
            Las horas no vencen. Una vez compradas no se devuelven.
          </p>
        </div>
      )}

      {balance.data && (
        <section>
          <h2 className="text-sm font-medium text-slate-500">
            Saldo: {formatHours(balance.data.hours)}
          </h2>
          {entries.length > 0 && (
            <ul className="mt-2 divide-y divide-slate-200 overflow-hidden rounded-lg border border-slate-200 bg-white text-sm">
              {entries.map((entry, index) => (
                <li key={index} className="flex items-center justify-between px-4 py-2">
                  <span>
                    {CREDIT_REASON_LABELS[entry.reason] ?? entry.reason}
                    <span className="ml-2 text-xs text-slate-500">
                      {formatInstant(entry.created_at)}
                    </span>
                  </span>
                  <span
                    className={[
                      'font-medium tabular-nums',
                      entry.hours > 0 ? 'text-green-700' : 'text-slate-600',
                    ].join(' ')}
                  >
                    {entry.hours > 0 ? '+' : ''}
                    {entry.hours}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  )
}
